import _slicedToArray from "C:\\Users\\Kristaps\\projects\\proj261\\site\\node_modules\\@babel\\runtime\\helpers\\esm\\slicedToArray.js";
var _excluded = ["className", "containerClassName", "disabled", "readOnly", "value", "min", "max", "incrementIcon", "decrementIcon", "placeholder", "autoFocus", "tabIndex", "parse", "name", "onChange", "messages", "format", "onKeyDown", "onKeyPress", "onKeyUp", "inputProps", "precision", "step"];

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

import cn from 'classnames';
import React, { useRef } from 'react';
import { useUncontrolled } from 'uncontrollable';
import Button from './Button';
import { caretDown, caretUp } from './Icon';
import { useLocalizer } from './Localization';
import NumberInput from './NumberInput';
import Widget from './Widget';
import WidgetPicker from './WidgetPicker';
import * as CustomPropTypes from './PropTypes';
import useFocusManager from './useFocusManager';
import { notify } from './WidgetHelpers';
import useEventCallback from '@restart/hooks/useEventCallback'; // my tests in ie11/chrome/FF indicate that keyDown repeats
// at about 35ms+/- 5ms after an initial 500ms delay. callback fires on the leading edge

function createInterval(callback) {
  var _fn;

  var id;

  var cancel = function cancel() {
    return clearTimeout(id);
  };

  id = window.setTimeout(_fn = function fn() {
    id = window.setTimeout(_fn, 35);
    callback(); //fire after everything in case the user cancels on the first call
  }, 500);
  return cancel;
}

function clamp(value, min, max) {
  max = max == null ? Infinity : max;
  min = min == null ? -Infinity : min;
  if (value == null || value === '') return null;
  return Math.max(Math.min(typeof value == 'string' ? parseInt(value) : value, max), min);
}

var defaultProps = {
  incrementIcon: caretUp,
  decrementIcon: caretDown,
  min: -Infinity,
  max: Infinity,
  step: 1,
  precision: 'auto'
};
/**
 * ---
 * localized: true
 * shortcuts:
 *   - { key: down arrow, label: decrement value }
 *   - { key: up arrow, label: increment value }
 *   - { key: home, label: set value to minimum value, if finite }
 *   - { key: end, label: set value to maximum value, if finite }
 * ---
 *
 * @public
 */

function NumberPicker(uncontrolledProps) {
  var _useUncontrolled = useUncontrolled(uncontrolledProps, {
    value: 'onChange'
  }),
      className = _useUncontrolled.className,
      containerClassName = _useUncontrolled.containerClassName,
      disabled = _useUncontrolled.disabled,
      readOnly = _useUncontrolled.readOnly,
      value = _useUncontrolled.value,
      min = _useUncontrolled.min,
      max = _useUncontrolled.max,
      incrementIcon = _useUncontrolled.incrementIcon,
      decrementIcon = _useUncontrolled.decrementIcon,
      placeholder = _useUncontrolled.placeholder,
      autoFocus = _useUncontrolled.autoFocus,
      tabIndex = _useUncontrolled.tabIndex,
      parse = _useUncontrolled.parse,
      name = _useUncontrolled.name,
      onChange = _useUncontrolled.onChange,
      messages = _useUncontrolled.messages,
      format = _useUncontrolled.format,
      onKeyDown = _useUncontrolled.onKeyDown,
      onKeyPress = _useUncontrolled.onKeyPress,
      onKeyUp = _useUncontrolled.onKeyUp,
      inputProps = _useUncontrolled.inputProps,
      precision = _useUncontrolled.precision,
      pStep = _useUncontrolled.step,
      elementProps = _objectWithoutPropertiesLoose(_useUncontrolled, _excluded);

  var localizer = useLocalizer(messages, {
    number: format
  });
  var ref = useRef(null);
  var inputRef = useRef(null);
  var repeaterRef = useRef(null);

  var _useFocusManager = useFocusManager(ref, uncontrolledProps, {
    willHandle: function willHandle(focused) {
      if (focused) focus();
    }
  }),
      _useFocusManager2 = _slicedToArray(_useFocusManager, 2),
      focusEvents = _useFocusManager2[0],
      focused = _useFocusManager2[1];

  var handleMouseDown = useEventCallback(function (direction, event) {
    if (event) event.persist();
    var method = direction === 'UP' ? increment : decrement;
    var value = method(event),
        atTop = direction === 'UP' && value === max,
        atBottom = direction === 'DOWN' && value === min;
    if (atTop || atBottom) handleMouseUp();else if (!repeaterRef.current) {
      repeaterRef.current = createInterval(function () {
        handleMouseDown(direction, event);
      });
    }
  });
  var handleMouseUp = useEventCallback(function () {
    if (!repeaterRef.current) return;
    repeaterRef.current();
    repeaterRef.current = null;
  });
  var handleKeyDown = useEventCallback(function (event) {
    if (readOnly) return;
    var key = event.key;
    notify(onKeyDown, [event]);
    if (event.defaultPrevented) return;
    if (key === 'End' && isFinite(max)) handleChange(max, event);else if (key === 'Home' && isFinite(min)) handleChange(min, event);else if (key === 'ArrowDown') {
      event.preventDefault();
      decrement(event);
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      increment(event);
    }
  });

  var handleChange = function handleChange(rawValue) {
    var originalEvent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var nextValue = clamp(rawValue, min, max);
    if (value !== nextValue) notify(onChange, [nextValue, {
      rawValue: rawValue,
      originalEvent: originalEvent,
      lastValue: value
    }]);
  };

  function focus() {
    var _inputRef$current;

    (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus();
  }

  function increment(event) {
    return step(pStep, event);
  }

  function decrement(event) {
    return step(-pStep, event);
  }

  function step(amount, event) {
    var nextValue = (value || 0) + amount;
    var p = precision === 'auto' ? Math.max(getPrecision(value || 0), getPrecision(amount)) : precision;
    handleChange(p != null ? parseFloat(nextValue.toFixed(p)) : nextValue, event);
    return nextValue;
  }

  var clampedValue = clamp(value, min, max);
  return /*#__PURE__*/React.createElement(Widget, _extends({}, elementProps, {
    focused: focused,
    disabled: disabled,
    readOnly: readOnly,
    onKeyDown: handleKeyDown
  }, focusEvents, {
    ref: ref,
    className: cn(className, 'rw-number-picker')
  }), /*#__PURE__*/React.createElement(WidgetPicker, {
    className: containerClassName
  }, /*#__PURE__*/React.createElement(NumberInput, _extends({}, inputProps, {
    role: "spinbutton",
    tabIndex: tabIndex,
    value: clampedValue,
    placeholder: placeholder,
    autoFocus: autoFocus,
    editing: focused,
    localizer: localizer,
    parse: parse,
    name: name,
    min: min,
    max: max,
    disabled: disabled,
    readOnly: readOnly,
    onChange: handleChange,
    onKeyPress: onKeyPress,
    onKeyUp: onKeyUp,
    innerRef: inputRef
  })), /*#__PURE__*/React.createElement("span", {
    className: "rw-input-addon rw-number-picker-spinners"
  }, /*#__PURE__*/React.createElement(Button, {
    icon: incrementIcon,
    className: "rw-picker-btn",
    disabled: clampedValue === max || disabled || readOnly,
    label: localizer.messages.increment({
      value: clampedValue,
      min: min,
      max: max
    }),
    onMouseUp: function onMouseUp() {
      return handleMouseUp();
    },
    onMouseDown: function onMouseDown(e) {
      return handleMouseDown('UP', e);
    },
    onMouseLeave: function onMouseLeave() {
      return handleMouseUp();
    }
  }), /*#__PURE__*/React.createElement(Button, {
    icon: decrementIcon,
    className: "rw-picker-btn",
    disabled: clampedValue === min || disabled || readOnly,
    label: localizer.messages.decrement({
      value: clampedValue,
      min: min,
      max: max
    }),
    onMouseUp: function onMouseUp() {
      return handleMouseUp();
    },
    onMouseDown: function onMouseDown(e) {
      return handleMouseDown('DOWN', e);
    },
    onMouseLeave: function onMouseLeave() {
      return handleMouseUp();
    }
  }))));
}

;
NumberPicker.defaultProps = defaultProps;
export default NumberPicker;

function getPrecision(a) {
  if (!isFinite(a)) return 0;
  var e = 1;
  var p = 0;

  while (Math.round(a * e) / e !== a) {
    e *= 10;
    p++;
  }

  return p;
}